'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Language, detectBrowserLanguage } from '@/lib/i18n';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const LANGUAGE_STORAGE_KEY = 'quantifyr_language';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'en',
  setLanguage: async () => {},
  isLoading: true,
});

export function useLanguage() {
  return useContext(LanguageContext);
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  // Spanish is the server-render default, and only the default: the effect
  // below still resolves the saved preference, then the browser's. It matters
  // because the landing page is rendered on the server for crawlers and for
  // the first paint, and this app is sold in Mexico — indexing an English page
  // under a Spanish title is the one outcome worth avoiding. Whichever way it
  // is set, somebody sees the other language for one frame; this way it is the
  // smaller group.
  const [language, setLanguageState] = useState<Language>('es');
  const [isLoading, setIsLoading] = useState(true);

  // Load language preference on mount / user change
  useEffect(() => {
    if (authLoading) return;

    async function loadLanguage() {
      setIsLoading(true);
      try {
        // 1. If user is logged in, try to get from Supabase
        if (user) {
          const supabase = createClient();
          const { data } = await supabase
            .from('business_profiles')
            .select('preferred_language')
            .eq('user_id', user.id)
            .maybeSingle();

          if (data?.preferred_language) {
            const lang = data.preferred_language as Language;
            setLanguageState(lang);
            if (typeof window !== 'undefined') {
              localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
            }
            setIsLoading(false);
            return;
          }
        }

        // 2. Try localStorage
        if (typeof window !== 'undefined') {
          const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
          if (stored === 'en' || stored === 'es') {
            setLanguageState(stored);
            setIsLoading(false);
            return;
          }
        }

        // 3. Auto-detect from browser
        const detected = detectBrowserLanguage();
        setLanguageState(detected);
        if (typeof window !== 'undefined') {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, detected);
        }
      } catch {
        // Fallback to browser detection
        const detected = detectBrowserLanguage();
        setLanguageState(detected);
      } finally {
        setIsLoading(false);
      }
    }

    loadLanguage();
  }, [user, authLoading]);

  const setLanguage = useCallback(async (lang: Language) => {
    setLanguageState(lang);

    // Save to localStorage immediately
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    }

    // Save to Supabase if user is logged in
    if (user) {
      try {
        const supabase = createClient();
        await supabase
          .from('business_profiles')
          .update({ preferred_language: lang })
          .eq('user_id', user.id);
      } catch {
        // Non-critical — localStorage already saved
      }
    }
  }, [user]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}
